'use client'

// React
import { useEffect, useRef, useState } from 'react'

// Animation
import { easeOut, motion, useMotionValue, useTransform } from 'framer-motion'

// Icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faQrcode } from '@fortawesome/free-solid-svg-icons'

import {
  ButtonLG,
  Container,
  fadeInUp,
  Section,
  Spotlight,
  staggerContainer,
} from 'design-studio'

// UI components
import { SectionHeader } from '@/components/ui/section-header'
import { TerminalBox } from '@/components/ui/terminal-box'

// Utils
import { NavigationData } from '@/components/data-links'
const TYPEWRITER_WORDS = [
  'algorithm',
  'behavior',
  'logic',
  'property',
  'invariant',
]

const STEP3_WORDS = [
  'x >= 0',
  'double x = x + x',
  'rev (rev x) = x',
  'x + 0 = x',
  'sorted (sort x)',
]

function StepBadge({ number }: { number: number }) {
  return (
    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-white text-base font-bold text-slate-900">
      {number}
    </span>
  )
}

function TypewriterWord({ words = TYPEWRITER_WORDS }: { words?: string[] }) {
  const [displayed, setDisplayed] = useState('')
  const [wordIdx, setWordIdx] = useState(0)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    const word = words[wordIdx]
    if (!deleting) {
      if (displayed.length < word.length) {
        const delay = 75 + Math.random() * 65
        const t = setTimeout(
          () => setDisplayed(word.slice(0, displayed.length + 1)),
          delay,
        )
        return () => clearTimeout(t)
      } else {
        const t = setTimeout(() => setDeleting(true), 950 + Math.random() * 450)
        return () => clearTimeout(t)
      }
    } else {
      if (displayed.length > 0) {
        const delay = 42 + Math.random() * 33
        const t = setTimeout(() => setDisplayed((d) => d.slice(0, -1)), delay)
        return () => clearTimeout(t)
      } else {
        const t = setTimeout(
          () => {
            setWordIdx((i) => (i + 1) % words.length)
            setDeleting(false)
          },
          280 + Math.random() * 120,
        )
        return () => clearTimeout(t)
      }
    }
  }, [displayed, deleting, wordIdx, words])

  return (
    <span className="text-teal-400">
      {displayed}
      <span className="ml-px inline-block w-[2px] translate-y-px animate-pulse bg-teal-400 align-middle text-transparent select-none">
        |
      </span>
    </span>
  )
}

export function HowToStart() {
  const [installMode, setInstallMode] = useState<'vscode' | 'python'>('vscode')
  const gridRef = useRef<HTMLDivElement>(null)
  const scrollYProgress = useMotionValue(0)

  useEffect(() => {
    const el = gridRef.current
    if (!el) return
    const update = () => {
      const top = el.getBoundingClientRect().top
      const vh = window.innerHeight
      const progress =
        1 - Math.min(1, Math.max(0, (top - vh * 0.5) / (vh * 0.5)))
      scrollYProgress.set(progress)
    }
    window.addEventListener('scroll', update, { passive: true })
    update()
    return () => window.removeEventListener('scroll', update)
  }, [scrollYProgress])

  const card2Y = useTransform(scrollYProgress, [0, 1], ['50%', '0%'])
  const card3Y = useTransform(scrollYProgress, [0, 1], ['100%', '0%'])
  const card2Opacity = useTransform(scrollYProgress, [0, 1], [0.5, 1])
  const card3Opacity = useTransform(scrollYProgress, [0, 1], [0.25, 1])

  const BORDER_BRIGHT = 'rgba(45, 212, 191, 0.85)'
  const BORDER_MID = 'rgba(45, 212, 191, 0.425)'
  const BORDER_FAINT = 'rgba(45, 212, 191, 0.255)'
  const BORDER_DIM = 'rgba(19, 78, 74, 0.25)'
  // rgb(141 71 215 / 10%)
  const card1Border = useTransform(
    scrollYProgress,
    [0, 0.2, 0.45],
    [BORDER_BRIGHT, BORDER_BRIGHT, BORDER_FAINT],
  )
  const card2Border = useTransform(
    scrollYProgress,
    [0.1, 0.33, 0.5, 0.67, 0.9],
    [BORDER_DIM, BORDER_DIM, BORDER_BRIGHT, BORDER_BRIGHT, BORDER_MID],
  )
  const card3Border = useTransform(
    scrollYProgress,
    [0.5, 0.72, 1],
    [BORDER_DIM, BORDER_DIM, BORDER_BRIGHT],
  )

  return (
    <Section
      id="steps"
      className="w-full scroll-mt-32 overflow-hidden lg:my-26"
    >
      <Container className="max-w-8xl relative">
        <motion.div
          id="start-now"
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="relative mx-auto mt-24 mb-6 scroll-mt-32"
        >
          <SectionHeader
            title="How to start"
            pretitle="Start now"
            srOnlyFullHeading="Start now How to start"
          />
        </motion.div>

        <div
          className="my-4 w-full overflow-hidden rounded-full"
          style={{ height: '1px' }}
        >
          <motion.div
            className="h-full bg-linear-to-r from-teal-900/40 to-blue-400"
            style={{
              scaleX: useTransform(scrollYProgress, [0, 0.85], [0, 1]),
              opacity: useTransform(scrollYProgress, [0.7, 1], [1, 0], {
                ease: easeOut,
              }),
              transformOrigin: 'left',
            }}
          />
        </div>

        <motion.div
          ref={gridRef}
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.3 }}
          className="relative z-20 grid grid-cols-1 gap-3 lg:grid-cols-3"
        >
          {/* Card 1 */}
          <motion.div
            variants={fadeInUp}
            className="col-span-1 space-y-2 rounded-xl border bg-teal-500/10 p-6 backdrop-blur-md"
            style={{ borderColor: card1Border }}
          >
            <p className="text-slate-300 text-shadow-sm">
              <span className="font-display mb-4 flex items-center gap-3 text-2xl text-white">
                <StepBadge number={1} />
                Sign in and get an API key
              </span>
              Your first step is to obtain an{' '}
              <a
                href={NavigationData.signup.href}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-blue-400 underline underline-offset-2 hover:text-blue-300"
              >
                Imandra Universe API key
              </a>{' '}
              that your instance of ImandraX will use for connecting.
            </p>
            <ButtonLG
              href={NavigationData.signup.href}
              variant="none"
              ariaLabel={NavigationData.signup['aria-label']}
              classNames="lg:ml-auto mt-2 font-display bg-teal-700 text-white font-bold whitespace-nowrap hover:bg-teal-600 max-w-max"
            >
              <FontAwesomeIcon icon={faQrcode} className="h-4 w-4" />
              Sign up
            </ButtonLG>
          </motion.div>

          {/* Card 2 */}
          <motion.div
            style={{ y: card2Y, opacity: card2Opacity }}
            className="h-full"
          >
            <motion.div
              variants={fadeInUp}
              className="col-span-1 h-full space-y-4 rounded-xl border bg-teal-500/10 p-6 backdrop-blur-md"
              style={{ borderColor: card2Border }}
            >
              <div className="flex items-center justify-between">
                <span className="font-display flex items-center gap-3 text-2xl text-white">
                  <StepBadge number={2} />
                  Install
                </span>
                <div className="flex rounded-lg border border-slate-700/50 bg-slate-800/50 p-0.5">
                  <button
                    onClick={() => setInstallMode('vscode')}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                      installMode === 'vscode'
                        ? 'bg-teal-600 text-white shadow'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    VS Code
                  </button>
                  <button
                    onClick={() => setInstallMode('python')}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition-all ${
                      installMode === 'python'
                        ? 'bg-teal-600 text-white shadow'
                        : 'text-slate-400 hover:text-slate-200'
                    }`}
                  >
                    Python
                  </button>
                </div>
              </div>

              {installMode === 'vscode' ? (
                <div className="space-y-2">
                  <TerminalBox copyContent="code --install-extension imandra.imandrax">
                    <span className="my-1 block">
                      <span className="text-teal-400">code</span>
                      <span className="text-orange-400">
                        {' '}
                        --install-extension{' '}
                      </span>
                      <span className="text-blue-400">imandra.imandrax</span>
                    </span>
                    <span className="text-xs text-slate-500">
                      # or search &quot;ImandraX&quot; in VS Code Extensions
                    </span>
                  </TerminalBox>
                  <p className="text-sm text-slate-400">
                    Goal-state visualization, incremental proof checking and LSP
                    integration. Works out of the box in{' '}
                    <a
                      href="https://marketplace.visualstudio.com/items?itemName=imandra.imandrax"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 underline-offset-2 hover:underline"
                    >
                      Cursor and Antigravity
                    </a>
                    .
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <TerminalBox copyContent="pip install imandrax-api">
                    <span className="my-1 block">
                      <span className="text-teal-400">pip</span>
                      <span className="text-blue-400"> install </span>
                      <span className="text-teal-300">imandrax-api</span>
                    </span>
                  </TerminalBox>
                  <p className="text-sm text-slate-400">
                    Full API access from Python — prove properties, synthesize
                    counterexamples and decompose state-spaces in notebooks and
                    CI pipelines.
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>

          {/* Card 3 */}
          <motion.div
            style={{ y: card3Y, opacity: card3Opacity }}
            className="h-full"
          >
            <motion.div
              variants={fadeInUp}
              className="col-span-1 h-full space-y-4 rounded-xl border bg-teal-500/10 p-6 backdrop-blur-md"
              style={{ borderColor: card3Border }}
            >
              <span className="font-display mb-4 flex items-center gap-3 text-2xl text-white">
                <StepBadge number={3} />
                Start reasoning
              </span>
              <TerminalBox copyContent="verify (fun x -> x >= 0)">
                <span className="my-1 block">
                  <span className="text-teal-400">verify</span>
                  <span className="text-slate-300"> (</span>
                  <span className="text-blue-400">fun</span>
                  <span className="text-slate-300"> x </span>
                  <span className="text-slate-400">-{'>'} </span>
                  <TypewriterWord words={STEP3_WORDS} />
                  <span className="text-slate-300">)</span>
                </span>
              </TerminalBox>
              <p className="text-base text-slate-300">
                ImandraX proves properties for all inputs — or finds a concrete
                counterexample.
              </p>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.p
          variants={fadeInUp}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="relative z-10 mx-auto mt-12 max-w-6xl text-base text-slate-600 dark:text-slate-300"
        >
          Read the full{' '}
          <a
            href="/docs/getting-started/"
            className="text-blue-400 underline-offset-2 hover:underline"
          >
            getting started guide here
          </a>
          .
        </motion.p>

        <Spotlight
          color="from-teal-600/10"
          className="-right-100 -bottom-100 z-0 size-256"
        />
      </Container>
    </Section>
  )
}
